/**
 *
 * UserPage
 *
 */
import * as React from 'react';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import CardLayout from 'app/components/CardLayout/index';
import { Box, Grid, useTheme, IconButton, MenuItem, Menu } from '@mui/material';
import IconText from 'app/components/IconText';
import Email from '@mui/icons-material/Email';
import Business from '@mui/icons-material/Business';
import { useDispatch, useSelector } from 'react-redux';
import Pagination from 'app/components/Pagination';
import {
  paginationActions,
  usePaginationSlice,
} from 'app/components/Pagination/slice/index';
import { useMenuSlice } from 'app/components/Menu/slice';
import { API_ENDPOINTS } from 'utils/configs';
import { selectPaginationByType } from 'app/components/Pagination/slice/selectors';
import { MemberStatus, User } from 'types/models';
import fonts from 'app/assets/styles/fonts';
import colors from 'app/assets/styles/colors';
import { AsseticChip, ChipColor } from '@assetic/web-components';
import _ from 'lodash';
import LightTooltip from 'app/components/LightTooltip';
import { useHistory } from 'react-router-dom';
import { UrlToUser } from 'app/constants';
import { PagiCardsLoadingEl } from 'app/components/PagiCardsLoadingEl';
import { PagiQueryTextField } from 'app/components/PagiQueryTextField';
import { makeThemeStyle } from 'styles/makeThemeStyle';
import { DeletionConfirmDialog } from '../../components/DeletionConfirmDialog';
import { userPageActions } from './slice';
import { useSubscriptionDetailPageSlice } from '../SubscriptionDetailPage/slice';
import { selectSubscriptionDetailPage } from '../SubscriptionDetailPage/slice/selectors';

interface Props {}

export const ChipColorMap = (label: MemberStatus | string): ChipColor => {
  switch (label) {
    case 'invited':
    case 'inactive':
      return 'default';
    case 'pending':
      return 'warning';
    case 'suspended':
      return 'error_a';
    case 'active':
      return 'ready';
    default:
      return 'primary';
  }
};

const useStyles = makeThemeStyle(theme => ({
  menuBtn: {
    padding: 0,
    marginTop: theme.spacing(0.5),
  },
}));

export function UserPage(_props: Props) {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const dispatch = useDispatch();
  const classes = useStyles();
  const theme = useTheme();
  usePaginationSlice();
  const { content, cur_page } = useSelector(
    selectPaginationByType('pagiUser'),
    _.isEqual,
  );
  const {
    actions: { changeTitle },
  } = useMenuSlice();
  const {
    actions: { fetchSubscription },
  } = useSubscriptionDetailPageSlice();

  const { subscription } = useSelector(selectSubscriptionDetailPage);
  React.useEffect(() => {
    dispatch(
      changeTitle({
        siteTitle: 'Users',
      }),
    );
  }, [dispatch, changeTitle]);

  React.useEffect(() => {
    dispatch(
      paginationActions.updateQueryInfo({
        filters: '(email|first_name|last_name)@=*',
        sorts: '',
        page: 0,
        page_size: 12,
        path: API_ENDPOINTS.USERS,
        pagiType: 'pagiUser',
      }),
    );
  }, [dispatch]);

  const history = useHistory();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [selectedUser, setSelectedUser] = React.useState<User | null>(null);

  const isUserHasSubscription = Boolean(
    selectedUser?.subscription_id &&
      subscription?.portfolio_usage &&
      subscription.portfolio_usage > 0,
  );
  const handleMenuClick = (
    event: React.MouseEvent<HTMLButtonElement>,
    user: User,
  ) => {
    setAnchorEl(event.currentTarget);
    setSelectedUser(user);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
    setSelectedUser(null);
  };

  const handleActivate = () => {
    if (selectedUser?.id) {
      dispatch(
        userPageActions.activateUser({
          userId: selectedUser.id,
        }),
      );
    }
    handleMenuClose();
  };
  const handleDeactivate = () => {
    if (selectedUser?.id) {
      dispatch(
        userPageActions.deactivateUser({
          userId: selectedUser.id,
        }),
      );
    }
    handleMenuClose();
  };
  const handleDelete = () => {
    getDeleteMessage(selectedUser);
    if (selectedUser?.subscription_id) {
      dispatch(fetchSubscription(selectedUser.subscription_id));

      setDeleteConfirmOpen(true);
    } else {
      setDeleteConfirmOpen(true);
    }
  };

  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);

  const deleteUserDialogClose = () => {
    setDeleteConfirmOpen(false);
    handleMenuClose();
  };

  const deleteUser = () => {
    if (selectedUser?.id) {
      dispatch(
        userPageActions.deleteUser({
          userId: selectedUser.id,
        }),
      );
      deleteUserDialogClose();
      handleMenuClose();
    }
  };

  const getDeleteMessage = user => {
    if (!user?.subscription_id) {
      return `Are you sure you want to permanently remove this user?`;
    } else if (isUserHasSubscription) {
      return `The user is a subscription owner with ${subscription?.portfolio_usage} portfolio usage. Please transfer all of the portfolios to a new owner before proceeding.`;
    } else if (user?.subscription_id && subscription?.portfolio_usage === 0) {
      return `Are you sure you want to permanently remove this user? The user owns a subscription with 0 portfolio usage, this will also be removed permanently.`;
    }
  };

  return (
    <>
      <PagiQueryTextField
        filterFields={['email', 'first_name', 'last_name']}
        path={API_ENDPOINTS.USERS}
        label={'Search users by name or email'}
        paginationType="pagiUser"
      />
      <Pagination loadingEl={<PagiCardsLoadingEl />} paginationType="pagiUser">
        {!content?.[cur_page]?.data?.length ? null : (
          <Grid container spacing={2}>
            {content[cur_page].data?.map((data: User, i) => (
              <Grid item xs={12} sm={12} md={6} lg={4} xl={3} key={i}>
                <CardLayout
                  action={
                    <>
                      <IconButton
                        className={classes.menuBtn}
                        data-testid={`user_card_menu_${i}`}
                        onClick={e => handleMenuClick(e, data)}
                        size="large"
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </>
                  }
                  title={
                    <Box
                      fontSize={fonts.title}
                      color={theme.palette.asseticPrimary.main}
                      textOverflow="ellipsis"
                      overflow="hidden"
                      whiteSpace="nowrap"
                    >
                      <LightTooltip
                        placement="top"
                        title={`${data.first_name} ${data.last_name}`}
                      >
                        <span>
                          {data.first_name} {data.last_name}
                        </span>
                      </LightTooltip>
                    </Box>
                  }
                  subheader={
                    <Box fontSize={fonts.small} whiteSpace="nowrap">
                      {data.auth_id}
                    </Box>
                  }
                  cardContent={
                    <Box
                      onClick={() => {
                        if (data.id) {
                          history.push(UrlToUser(data.id).detail);
                        } else {
                          //TODO:Probably dispatch err toaster on userId not exist
                          console.log('No User Id');
                        }
                      }}
                    >
                      <Box mb={1}>
                        <IconText
                          icon={<Email />}
                          text={
                            <LightTooltip
                              placement="top"
                              title={`${data.email}`}
                            >
                              <span>{data.email}</span>
                            </LightTooltip>
                          }
                        />
                      </Box>
                      <Box mb={1}>
                        <IconText
                          icon={<Business />}
                          text={data.organisation}
                        />
                      </Box>
                      <Box display="flex" lineHeight={'24px'}>
                        <Box color={colors.silverGray} pr={1}>
                          Segment
                        </Box>
                        <Box color={colors.arcaneNavy} fontWeight={500}>
                          {data.segment_name || 'No Segment'}
                        </Box>
                      </Box>
                    </Box>
                  }
                  cardAction={
                    <>
                      <AsseticChip
                        label={data.status || 'pending'}
                        uppercase
                        color={ChipColorMap(data.status || '')}
                      />

                      {!data.subscription_id || (
                        <AsseticChip
                          label={'has subscription'}
                          capitalize
                          color={'primary'}
                        />
                      )}
                    </>
                  }
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Pagination>
      {selectedUser && (
        <Menu
          id="basic-menu"
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={handleMenuClose}
          MenuListProps={{
            'aria-labelledby': 'basic-button',
          }}
        >
          <>
            {(selectedUser.status === 'inactive' ||
              selectedUser.status === 'pending') && (
              <MenuItem onClick={handleActivate}>Activate User</MenuItem>
            )}
            {selectedUser.status === 'active' && (
              <MenuItem onClick={handleDeactivate}>Deactivate User</MenuItem>
            )}
            <MenuItem onClick={handleDelete}>Delete User</MenuItem>
          </>
        </Menu>
      )}
      <DeletionConfirmDialog
        onAccept={deleteUser}
        onClose={deleteUserDialogClose}
        open={deleteConfirmOpen}
        title="Delete User"
        confirmValue="Delete"
        message={getDeleteMessage(selectedUser)}
        requireValidate={!isUserHasSubscription}
        showSubmitButton={!isUserHasSubscription}
        cancelButtonTextOverride={isUserHasSubscription ? 'Ok' : 'Cancel'}
      />
    </>
  );
}

export default UserPage;
